import { useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import type { PreviewInfinito } from "@/features/reporteria/hooks/use-preview";
import { TAMANO_BLOQUE } from "@/features/reporteria/hooks/use-preview";
import { esEjecutable, firmaDef } from "@/features/reporteria/state/builder-ops";
import type { RepDefinicion } from "@/features/reporteria/services/reporteria.api";
import { defCatalogo, defContar, defListar, defPreview, defRelaciones } from "../services/admin-reporteria.api";

const MAX_BLOQUES = 6;
const DEBOUNCE_MS = 400;

function esConflicto(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 409;
}

function useDebounced<T>(valor: T, ms: number): T {
  const [debounced, setDebounced] = useState(valor);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(valor), ms);
    return () => clearTimeout(id);
  }, [valor, ms]);
  return debounced;
}

export function useDefCatalogo(idConfig: number) {
  return useQuery({
    queryKey: qk.repAdminDefCatalogo(idConfig),
    queryFn: () => defCatalogo(idConfig),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function useDefRelaciones(idConfig: number) {
  return useQuery({
    queryKey: qk.repAdminDefRelaciones(idConfig),
    queryFn: () => defRelaciones(idConfig),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

export function usePredefinidosDeConfig(idConfig: number) {
  return useQuery({
    queryKey: qk.repAdminPredefinidos(idConfig),
    queryFn: () => defListar(idConfig),
  });
}

/**
 * Preview con scroll infinito + conteo diferido EN CONTEXTO de una configuración
 * (mismo comportamiento que el del usuario, pero contra los endpoints admin).
 * Devuelve la misma forma que <c>usePreviewInfinito</c> para reusar PreviewTabla.
 */
export function useDefPreviewInfinito(idConfig: number, def: RepDefinicion, activo: boolean): PreviewInfinito {
  const defDebounced = useDebounced(def, DEBOUNCE_MS);
  const firma = firmaDef(defDebounced);
  const habilitado = activo && esEjecutable(defDebounced);

  const preview = useInfiniteQuery({
    queryKey: qk.repAdminDefPreview(idConfig, firma),
    enabled: habilitado,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      defPreview(idConfig, { def: defDebounced, pagina: pageParam, tamano: TAMANO_BLOQUE, incluirTotal: false, muestra: false }),
    getNextPageParam: (ultima, paginas) => (ultima.filas.length < TAMANO_BLOQUE ? undefined : paginas.length + 1),
    getPreviousPageParam: (_p, _ps, primerParam) => (primerParam > 1 ? primerParam - 1 : undefined),
    maxPages: MAX_BLOQUES,
    staleTime: 30_000,
    retry: (n, error) => esConflicto(error) && n < 5,
    retryDelay: (n) => 300 * (n + 1),
  });

  // El conteo espera al primer bloque del preview: evita el choque con el candado (RN-08).
  const total = useQuery({
    queryKey: qk.repAdminDefTotal(idConfig, firma),
    enabled: habilitado && preview.isSuccess,
    queryFn: () => defContar(idConfig, defDebounced),
    staleTime: 60_000,
    retry: (n, error) => esConflicto(error) && n < 5,
    retryDelay: (n) => 300 * (n + 1),
  });

  const columnas = preview.data?.pages[0]?.columnas ?? [];
  const filas = preview.data?.pages.flatMap((p) => p.filas) ?? [];
  const totales = preview.data?.pages[0]?.totales ?? null;

  return { preview, total, columnas, filas, totales };
}
